import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AddToBag, SoldOutBadge } from "@/components/AddToBag";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { NEW_COLLECTION, NEW_LAUNCHES, collectionUrl, discountPct, formatINR, productUrl } from "@/data/catalog";
import { useLiveMeta, useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const Feature = ({ p: base }) => {
  const p = useLiveProduct(base);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const off = discountPct(p.price, p.compare);
  return (
    <Reveal className="lg:col-span-5">
      <a ref={ref} href={productUrl(p.handle)} data-testid={`new-launch-feature-${p.id}`} className="group relative block aspect-[4/5] overflow-hidden bg-card">
        <motion.img style={{ y, scale: 1.18 }} src={p.img} alt={p.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        {p.alt && <img src={p.alt} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100" />}
        <span className="absolute left-4 top-4 rounded-full bg-champagne px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-ink">New · {p.date}</span>
        <SoldOutBadge p={p} className="absolute right-4 top-4" />
      </a>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-3xl text-cream sm:text-4xl">{p.name}</h3>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.16em] text-stone">{p.note}</p>
        </div>
        <p className="text-right text-cream" data-testid={`new-launch-price-${p.id}`}>
          {formatINR(p.price)}
          {off > 0 && <span className="block text-xs text-stone line-through">{formatINR(p.compare)}</span>}
        </p>
      </div>
      <AddToBag p={p} testId={`new-launch-add-${p.id}`} className="btn-bag mt-5" />
    </Reveal>
  );
};

const Row = ({ p: base, i, onHover }) => {
  const p = useLiveProduct(base);
  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
    >
      <a
        href={productUrl(p.handle)}
        data-testid={`new-launch-row-${p.id}`}
        onMouseEnter={() => onHover(p)}
        onMouseLeave={() => onHover(null)}
        className="group grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b border-cream/15 py-5 transition-colors duration-500 hover:bg-cream/[0.03] sm:gap-6 sm:py-7"
      >
        <img src={p.thumb || p.img} alt="" loading="lazy" className="h-20 w-16 object-cover sm:h-24 sm:w-20" />
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-champagne">
            New · {p.date}
            {p.available === false && <span className="ml-3 text-stone">· Sold out</span>}
          </p>
          <h3 className="mt-1 font-display text-2xl leading-tight text-cream transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl lg:text-4xl">{p.name}</h3>
          <p className="mt-1 text-xs text-stone sm:text-sm">{p.note}</p>
        </div>
        <div className="flex items-center gap-3 text-right sm:gap-5">
          <p className="text-sm text-cream sm:text-base" data-testid={`new-launch-price-${p.id}`}>
            {formatINR(p.price)}
            {p.compare > p.price && <span className="block text-xs text-stone line-through">{formatINR(p.compare)}</span>}
          </p>
          <span className="hidden h-11 w-11 place-items-center rounded-full border border-champagne/40 text-champagne transition-all duration-500 group-hover:rotate-45 group-hover:bg-champagne group-hover:text-ink sm:grid">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </a>
    </motion.li>
  );
};

export const NewLaunches = () => {
  const { newAdditions } = useLiveMeta();
  const [hover, setHover] = useState(null);
  const x = useSpring(useMotionValue(0), { stiffness: 250, damping: 28 });
  const y = useSpring(useMotionValue(0), { stiffness: 250, damping: 28 });
  // Live Shopify collection (auto-syncs as products are added); the static list only covers the first paint / outages.
  const live = Boolean(newAdditions?.length);
  const [feature, ...rest] = live ? newAdditions : NEW_LAUNCHES;

  const onMove = (e) => {
    x.set(e.clientX);
    y.set(e.clientY);
  };

  return (
    <section id="new" data-testid="new-launches-section" data-live={live ? "true" : "false"} data-count={1 + rest.length} className="theme-light relative bg-ink text-cream">
      <div className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8 lg:px-12 lg:py-36">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-8">
        <div>
          <Eyebrow>04 — Just pressed</Eyebrow>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
            <MaskText lines={["New launches,", <em key="e" className="italic text-champagne">fresh from the studio.</em>]} />
          </h2>
        </div>
        <Reveal>
          <a href={collectionUrl(NEW_COLLECTION)} data-testid="new-launches-view-all" className="nav-link inline-flex items-center gap-1 text-sm text-cream">
            Shop all new additions <ArrowUpRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Feature p={feature} />
        <ul className="border-t border-cream/15 lg:col-span-7" onMouseMove={onMove}>
          {rest.map((p, i) => (
            <Row key={p.id} p={p} i={i} onHover={setHover} />
          ))}
          <li className="pt-8">
            <a href={collectionUrl(NEW_COLLECTION)} data-testid="new-launches-collection-link" className="btn-ghost">
              See everything new in the Beauty Edit <ArrowUpRight className="h-4 w-4" />
            </a>
          </li>
        </ul>
      </div>
      </div>
      <motion.div style={{ x, y }} className="pointer-events-none fixed left-0 top-0 z-40 hidden lg:block">
        <AnimatePresence>
          {hover && (
            <motion.img
              key={hover.id}
              src={hover.img}
              alt=""
              initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="absolute -translate-x-1/2 -translate-y-1/2 h-64 w-52 object-cover shadow-2xl"
              style={{ left: 140, top: -40 }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};
